import { useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, HelpCircle, Info, XCircle } from 'lucide-react'
import { useEscapeKey, useFocusTrap, useScrollLock } from '@/ui/kit'
import { cn } from './utils'
import { BORDER, FOCUS_RING, RADIUS, RADIUS_MD } from './presets'
import { mountDetached } from './reactRootHost'

const ICONS = {
  confirm: { Icon: HelpCircle, color: 'var(--warning-color)' },
  info: { Icon: Info, color: 'var(--info-blue)' },
  success: { Icon: CheckCircle2, color: 'var(--success-green)' },
  error: { Icon: XCircle, color: 'var(--error-color)' },
  warning: { Icon: AlertTriangle, color: 'var(--warning-color)' },
}

const buttonBase = cn(
  'inline-flex min-h-[var(--control-height)] items-center justify-center px-4 text-sm font-medium',
  RADIUS,
  FOCUS_RING
)

/**
 * Self-contained confirm dialog. Deliberately does not import Modal, so the
 * static API stays free of a circular import.
 */
const ConfirmDialog = ({ variant, config, onClose }) => {
  const {
    title,
    content,
    okText = 'OK',
    cancelText = 'Annuler',
    okType = 'primary',
    okButtonProps,
    cancelButtonProps,
    onOk,
    onCancel,
    icon,
    width = 416,
    centered = true,
    maskClosable = false,
    keyboard = true,
    closable = false,
    zIndex,
  } = config

  const dialogRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const { Icon, color } = ICONS[variant] || ICONS.info
  const showCancel = config.okCancel ?? variant === 'confirm'
  const rootZIndex = zIndex ?? 'var(--z-modal, 2000)'

  useScrollLock(true)
  useFocusTrap(dialogRef, true)

  const dismiss = async (handler) => {
    // antd keeps the dialog open while a returned promise is pending.
    const result = handler?.(onClose)
    if (result && typeof result.then === 'function') {
      try {
        setBusy(true)
        await result
      } catch {
        setBusy(false)
        return
      }
    }
    onClose()
  }

  useEscapeKey(() => keyboard && dismiss(onCancel), true)

  return (
    <div className="ui-modal-root fixed inset-0" style={{ zIndex: rootZIndex }}>
      <div
        className="ui-modal-mask fixed inset-0 bg-[var(--overlay-bg)]"
        role="presentation"
        onClick={maskClosable ? () => dismiss(onCancel) : undefined}
      />
      <div
        className={cn(
          'ui-modal-wrap fixed inset-0 flex justify-center overflow-y-auto p-4',
          centered ? 'items-center' : 'pt-[100px]'
        )}
      >
        <div
          ref={dialogRef}
          role="alertdialog"
          aria-modal="true"
          aria-label={typeof title === 'string' ? title : 'Confirmation'}
          tabIndex={-1}
          className={cn(
            'ui-modal ui-modal-confirm my-auto w-full bg-[var(--surface-card)] p-4 shadow-[var(--shadow-xl)]',
            RADIUS_MD
          )}
          style={{ maxWidth: typeof width === 'number' ? `${width}px` : width }}
        >
          <div className="ui-modal-confirm-body flex items-start gap-3">
            <span className="ui-modal-confirm-icon mt-0.5 shrink-0" style={{ color }}>
              {icon ?? <Icon size={22} aria-hidden="true" />}
            </span>
            <div className="min-w-0 flex-1">
              {title ? (
                <div className="ui-modal-confirm-title text-base font-semibold text-[var(--text-dark)]">
                  {title}
                </div>
              ) : null}
              {content ? (
                <div className="ui-modal-confirm-content mt-1.5 text-sm text-[var(--text-body)]">
                  {content}
                </div>
              ) : null}
            </div>
            {closable ? (
              <button
                type="button"
                aria-label="Fermer"
                onClick={() => dismiss(onCancel)}
                className="shrink-0 rounded p-1 text-[var(--gray-600)] hover:text-[var(--text-body)]"
              >
                &times;
              </button>
            ) : null}
          </div>

          <div className={cn('ui-modal-confirm-btns mt-4 flex justify-end gap-2 border-t pt-3', BORDER)}>
            {showCancel ? (
              <button
                type="button"
                onClick={() => dismiss(onCancel)}
                className={cn(
                  buttonBase,
                  'ui-btn border border-[var(--border-strong)] bg-[var(--surface-card)] text-[var(--text-body)] hover:border-[var(--primary-color)]'
                )}
                {...cancelButtonProps}
              >
                {cancelText}
              </button>
            ) : null}
            <button
              type="button"
              autoFocus
              disabled={busy}
              onClick={() => dismiss(onOk)}
              className={cn(
                buttonBase,
                'ui-btn ui-btn-primary border text-white',
                okType === 'danger'
                  ? 'border-[var(--error-color)] bg-[var(--error-color)]'
                  : 'border-[var(--primary-color)] bg-[var(--primary-color)]',
                busy && 'cursor-wait opacity-70'
              )}
              {...okButtonProps}
            >
              {okText}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const instances = new Set()

const open = (variant, config = {}) => {
  const handle = mountDetached((close) => (
    <ConfirmDialog
      variant={variant}
      config={config}
      onClose={() => {
        close()
        instances.delete(handle)
        config.afterClose?.()
      }}
    />
  ))

  instances.add(handle)

  return {
    destroy: () => {
      handle.destroy()
      instances.delete(handle)
    },
    update: (next) => {
      const merged = typeof next === 'function' ? next(config) : { ...config, ...next }
      handle.update((close) => (
        <ConfirmDialog
          variant={variant}
          config={merged}
          onClose={() => {
            close()
            instances.delete(handle)
          }}
        />
      ))
    },
  }
}

export const confirm = (config) => open('confirm', { okCancel: true, ...config })
export const info = (config) => open('info', config)
export const success = (config) => open('success', config)
export const error = (config) => open('error', config)
export const warning = (config) => open('warning', config)

export const destroyAll = () => {
  instances.forEach((handle) => handle.destroy())
  instances.clear()
}

export default { confirm, info, success, error, warning, destroyAll }
